<template>
  <div
    class="w-full h-auto min-h-screen mt-3 justify-between bg-white space-y-9 rounded-t-[20px]"
  >
    <HeaderComponent page-title="Learning Plan Details" />
    <div class="mx-8 pb-10">
      <div v-if="loading" class="text-center py-10">
        <Loader />
      </div>
      <EmptyState
        v-else-if="error"
        title="Oops! Something went wrong."
        :description="error"
      >
        <template #icon>
          <svg
            class="w-10 h-10 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </template>
        <template #action>
          <button
            @click="$router.push('/learning-plans')"
            class="bg-black text-white px-6 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            Go Back
          </button>
        </template>
      </EmptyState>
      <div v-else-if="template">
        <!-- Header -->
        <div class="flex items-center justify-between mb-2">
          <div class="flex flex-col">
            <h1 class="text-[20px] font-bold text-gray-900">
              {{ template.topic }}
            </h1>
            <p class="text-gray-500 text-[13px] mt-1">
              Created {{ formatDate(template.created_at) }}
            </p>
          </div>
          <button
            @click="openAssignModal"
            class="bg-black text-white px-6 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            Assign to Team
          </button>
        </div>

        <!-- Description Box -->
        <div
          class="w-full min-w-0 bg-transparent border border-gray-200 rounded-[12px] p-4 mb-6"
        >
          <div class="flex items-start gap-2">
            <p
              class="text-gray-700 text-[14px] flex-1 break-words whitespace-pre-wrap"
              :class="{ 'line-clamp-2': !descriptionExpanded }"
            >
              {{ template.description }}
            </p>
            <button
              type="button"
              class="h-6 w-6 flex-shrink-0 flex items-center justify-center hover:bg-gray-100 transition-colors rounded"
              @click="toggleDescription"
              :aria-expanded="descriptionExpanded.toString()"
              aria-label="Toggle description"
            >
              <svg
                :class="{ 'rotate-180': descriptionExpanded }"
                class="transition-transform text-gray-500"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Syllabus Grid -->
        <div>

          <div v-if="syllabusGroups.length > 0" class="space-y-10">
            <div v-for="group in syllabusGroups" :key="group.id">
              <!-- Group Header -->
              <div
                :class="[
                  'flex items-center gap-4 mb-5 p-4 rounded-[12px] border',
                  group.containerBg,
                  group.borderColor,
                ]"
              >
                <div
                  :class="[
                    'w-12 h-12 rounded-[10px] flex items-center justify-center',
                    group.iconBg,
                    group.iconColor,
                  ]"
                >
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    v-html="group.iconSvg"
                  ></svg>
                </div>
                <div>
                  <h3 class="text-[18px] font-bold text-gray-900">
                    {{ group.title }}
                  </h3>
                  <p class="text-[14px] text-gray-500">
                    {{ group.description }}
                  </p>
                </div>
              </div>

              <!-- Group Grid -->
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div
                  v-for="(task, index) in group.tasks"
                  :key="task.id"
                  class="bg-white border border-gray-200 rounded-[12px] p-4 transition-all duration-300 ease-in-out hover:shadow-md flex flex-col"
                >
                  <div class="flex items-start justify-between mb-4">
                    <div class="flex items-start space-x-3 flex-1">
                      <div
                        class="mt-1 flex-shrink-0 w-[30px] h-[30px] rounded-[15px] border flex items-center justify-center bg-gray-50 border-gray-200 text-sm font-medium text-gray-600"
                      >
                        {{ index + 1 }}
                      </div>
                      <div class="flex-1">
                        <h3
                          class="text-[16px] font-semibold text-gray-900 mb-1 leading-tight"
                        >
                          {{ task.title }}
                        </h3>
                        <p
                          class="text-[13px] text-gray-500 mb-2 line-clamp-2"
                          :title="task.description"
                        >
                          {{ task.description }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Subtasks list -->
                  <div
                    v-if="(task.subtasks && task.subtasks.length > 0) || (task.sub_tasks && task.sub_tasks.length > 0)"
                    class="mt-auto pt-4 border-t border-gray-100 space-y-2"
                  >
                    <div
                      v-for="sub in (task.sub_tasks || task.subtasks).slice(0, 3)"
                      :key="sub.id || sub.task_id"
                      class="flex gap-2 items-start"
                    >
                      <svg
                        class="w-4 h-4 text-gray-400 shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p
                        class="text-[12px] text-gray-600 truncate"
                        :title="sub.title"
                      >
                        {{ sub.title }}
                      </p>
                    </div>
                    <div
                      v-if="(task.sub_tasks || task.subtasks).length > 3"
                      class="text-[12px] text-gray-400 font-medium pl-6"
                    >
                      + {{ (task.sub_tasks || task.subtasks).length - 3 }} more subtasks
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="text-center py-12 bg-gray-50 rounded-[12px] border border-dashed border-gray-300 text-gray-500"
          >
            This learning plan doesn't have any lessons yet.
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Modal -->
    <AssignTemplateModal
      :showModal="isAssignModalOpen"
      :template="template"
      :teamId="resolvedTeamId || teamId"
      @close="isAssignModalOpen = false"
      @assigned="handleAssigned"
    />
  </div>
</template>

<script>
import HeaderComponent from "../HeaderComponent.vue";
import apiClient from "@/axios";
import Loader from "@/components/ui/Loader.vue";
import AssignTemplateModal from "../modals/AssignTemplateModal.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import { mapState } from "vuex";

export default {
  name: "LearningPlanDetailsComponent",
  components: {
    HeaderComponent,
    Loader,
    AssignTemplateModal,
    EmptyState,
  },
  props: {
    templateId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      template: null,
      loading: true,
      error: null,
      isAssignModalOpen: false,
      resolvedTeamId: null,
      descriptionExpanded: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
    teamId() {
      // Assuming user has a default team or we get it from local storage
      return localStorage.getItem("activeTeamId") || this.user?.teams?.[0]?.id;
    },
    syllabusGroups() {
      if (!this.template?.tasks) return [];
      
      const groups = {
        lessons: {
          id: 'lessons',
          title: 'Lessons',
          description: 'Core learning materials and concepts.',
          iconBg: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
          containerBg: 'bg-emerald-50/50',
          borderColor: 'border-emerald-100',
          iconSvg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />',
          tasks: []
        },
        projects: {
          id: 'projects',
          title: 'Projects',
          description: 'Hands-on practice to apply what you have learned.',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          containerBg: 'bg-blue-50/50',
          borderColor: 'border-blue-100',
          iconSvg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />',
          tasks: []
        },
        assessments: {
          id: 'assessments',
          title: 'Assessments',
          description: 'Quizzes and evaluations to measure your progress.',
          iconBg: 'bg-purple-100',
          iconColor: 'text-purple-600',
          containerBg: 'bg-purple-50/50',
          borderColor: 'border-purple-100',
          iconSvg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />',
          tasks: []
        }
      };

      this.template.tasks.forEach(task => {
        let type = task.learning_task_type?.toLowerCase() || 'lesson';
        if (type === 'project' || type === 'capstone_project') {
          groups.projects.tasks.push(task);
        } else if (type === 'assessment' || type === 'quiz') {
          groups.assessments.tasks.push(task);
        } else {
          groups.lessons.tasks.push(task);
        }
      });

      if (this.template?.has_assessment) {
        groups.assessments.tasks.push({
          task_id: 'assessment-final',
          title: 'Final Assessment',
          description: 'A comprehensive evaluation of the skills you have learned in this learning plan. Consists of a multiple-choice quiz and an oral examination.',
          sub_tasks: [
            { id: 'mcq', title: 'Multiple Choice Quiz' },
            { id: 'oral', title: 'Oral Examination' }
          ]
        });
      }

      return [groups.lessons, groups.projects, groups.assessments].filter(g => g.tasks.length > 0);
    },
  },
  mounted() {
    this.fetchTemplateDetails();
  },
  methods: {
    async fetchTemplateDetails() {
      this.loading = true;
      this.error = null;
      try {
        let teamId = this.teamId;
        if (!teamId) {
          const teamResponse = await apiClient.get("/web/teams/manager/teams");
          const teamsData = teamResponse.data?.data;
          if (teamsData && teamsData.length > 0) {
            teamId = teamsData[0].id || teamsData[0].team_id;
            // Also save to localStorage so subsequent navigations work
            localStorage.setItem("activeTeamId", teamId);
          }
        }

        if (!teamId) {
          throw new Error("No active team found");
        }

        this.resolvedTeamId = teamId; // Use resolvedTeamId so AssignTemplateModal gets a valid ID

        const response = await apiClient.get(
          `/web/teams/${teamId}/templates/${this.templateId}`,
        );
        if (response.data && response.data.data) {
          this.template = response.data.data;
        }
      } catch (err) {
        console.error("Error fetching template details:", err);
        this.error =
          err.response?.data?.message ||
          "Failed to load learning plan details.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    calculateTotalLessons(tasks) {
      if (!tasks) return 0;
      let count = 0;
      tasks.forEach((task) => {
        const subs = task.sub_tasks || task.subtasks;
        if (subs && subs.length > 0) {
          count += subs.length;
        } else {
          count += 1;
        }
      });
      return count;
    },
    calculateTotalPracticeTasks(tasks) {
      if (!tasks) return 0;
      let count = 0;
      tasks.forEach((task) => {
        if (task.practice_task) count++;
        const subs = task.sub_tasks || task.subtasks;
        if (subs && subs.length > 0) {
          subs.forEach((sub) => {
            if (sub.practice_task) count++;
          });
        }
      });
      return count;
    },
    openAssignModal() {
      this.isAssignModalOpen = true;
    },
    handleAssigned() {
      // You can add a toast notification here if needed
      this.isAssignModalOpen = false;
    },
  },
};
</script>

<style scoped></style>
